import React from 'react';
import { motion } from 'framer-motion';
import { RELATION_COLORS } from './familyTreeData';

const AVATAR_FALLBACK = (name = '?') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1E3A5F&color=C9A84C&bold=true&size=128`;

// Cousin nodes get a distinct rose/pink ring accent
const COUSIN_RELATIONS = ['Cousin', 'Cousin (once removed)', 'Cousin (twice removed)'];

const TreeNode = ({ node, x, y, isSelected, isHighlighted, isDimmed, onClick, verificationStatus }) => {
  const size     = node.isDeceased ? 52 : 48;
  const half     = size / 2;
  const isRoot   = node.relation === 'Deceased';
  const isCousin = COUSIN_RELATIONS.includes(node.relation);

  // Border colour: selected → blue, root → gold, cousin → pink, else default
  const borderColor = isSelected
    ? '#3B82F6'
    : isRoot
      ? '#C9A84C'
      : isCousin
        ? RELATION_COLORS.cousin
        : node.isDeceased
          ? '#C9A84C'
          : '#D4C5A0';

  // Verification badge color
  const verifColor = {
    verified:   '#059669',
    pending:    '#D97706',
    unverified: 'transparent',
  }[verificationStatus] ?? 'transparent';

  const showVerifBadge = verificationStatus && verificationStatus !== 'unverified';

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{
        opacity: isDimmed ? 0.22 : 1,
        scale: isSelected ? 1.15 : 1,
      }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(node)}
    >
      {/* Animated ring: root / selected */}
      {(isRoot || isSelected) && (
        <motion.circle
          cx={x} cy={y} r={half + 10}
          fill="none"
          stroke={isSelected ? '#3B82F6' : '#C9A84C'}
          strokeWidth={2}
          strokeDasharray="6 4"
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: `${x}px ${y}px` }}
          opacity={0.6}
        />
      )}

      {/* Dashed cousin ring */}
      {isCousin && !isSelected && (
        <circle
          cx={x} cy={y} r={half + 7}
          fill="none"
          stroke={RELATION_COLORS.cousin}
          strokeWidth={1.5}
          strokeDasharray="5 3"
          opacity={isHighlighted ? 0.85 : 0.4}
        />
      )}

      {/* Highlight ring for connected nodes */}
      {isHighlighted && !isSelected && !isCousin && (
        <circle cx={x} cy={y} r={half + 6}
          fill="none" stroke="#C9A84C" strokeWidth={2} opacity={0.5} />
      )}

      {/* Verification outer ring */}
      {showVerifBadge && (
        <circle
          cx={x} cy={y} r={half + (isSelected ? 14 : 12)}
          fill="none"
          stroke={verifColor}
          strokeWidth={2}
          strokeDasharray={verificationStatus === 'pending' ? '3 3' : undefined}
          opacity={isSelected ? 0.9 : 0.6}
        />
      )}

      {/* Clip path */}
      <defs>
        <clipPath id={`clip-${node.id}`}>
          <circle cx={x} cy={y} r={half} />
        </clipPath>
      </defs>

      {/* Background */}
      <circle
        cx={x} cy={y} r={half}
        fill={node.isDeceased ? '#1E3A5F' : isCousin ? '#FDF2F8' : '#F5F0E8'}
        stroke={borderColor}
        strokeWidth={isRoot || isSelected || isCousin ? 2.5 : 2}
      />

      {/* Avatar */}
      <image
        href={node.avatar || AVATAR_FALLBACK(node.name)}
        x={x - half} y={y - half}
        width={size} height={size}
        clipPath={`url(#clip-${node.id})`}
        preserveAspectRatio="xMidYMid slice"
      />

      {/* Deceased overlay */}
      {node.isDeceased && (
        <circle cx={x} cy={y} r={half}
          fill="rgba(30,58,95,0.45)"
          clipPath={`url(#clip-${node.id})`} />
      )}

      {/* Cousin indicator badge */}
      {isCousin && (
        <g transform={`translate(${x + half - 6}, ${y - half + 6})`}>
          <circle r={7} fill={RELATION_COLORS.cousin} stroke="white" strokeWidth={1.5} />
          <text textAnchor="middle" dominantBaseline="central"
            fontSize={7} fontWeight="900" fill="white">C</text>
        </g>
      )}

      {/* Verification badge on avatar */}
      {showVerifBadge && (
        <g transform={`translate(${x + half - 4}, ${y + half - 4})`}>
          <circle r={6} fill={verifColor} stroke="white" strokeWidth={1.5} />
          <text textAnchor="middle" dominantBaseline="central"
            fontSize={5} fontWeight="900" fill="white">
            {verificationStatus === 'verified' ? '✓' : '?'}
          </text>
        </g>
      )}

      {/* Name */}
      <text
        x={x} y={y + half + 16}
        textAnchor="middle"
        fontSize={node.isDeceased ? 11 : 10}
        fontWeight={node.isDeceased ? '700' : '600'}
        fill={node.isDeceased ? '#1E3A5F' : '#374151'}
        fontFamily="serif"
      >
        {node.name.split(' ').slice(0, 2).join(' ')}
      </text>

      {/* Relation badge */}
      <text
        x={x} y={y + half + 30}
        textAnchor="middle"
        fontSize={8}
        fontWeight="700"
        fill={isCousin ? RELATION_COLORS.cousin : '#C9A84C'}
        letterSpacing="0.08em"
        style={{ textTransform: 'uppercase' }}
      >
        {node.relation.length > 14 ? node.relation.slice(0, 12) + '…' : node.relation}
      </text>

      {/* Year range */}
      <text
        x={x} y={y + half + 42}
        textAnchor="middle"
        fontSize={8}
        fill="#9CA3AF"
        fontFamily="monospace"
      >
        {node.birthYear}{node.deathYear ? ` – ${node.deathYear}` : ''}
      </text>
    </motion.g>
  );
};

export default TreeNode;